import { useEffect, useRef, useState } from 'react';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import Icon from '../../components/icon/Icon';

export type EditableCommissionInterval = {
  fromMonth: string;
  toMonth: string;
  rate: string;
};

type CommissionIntervalsEditorProps = {
  intervals: EditableCommissionInterval[];
  onAddInterval: () => void;
  onRemoveInterval: (intervalIndex: number) => void;
  onIntervalChange: (
    intervalIndex: number,
    field: keyof EditableCommissionInterval,
    value: string,
  ) => void;
};

type IntervalInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  'aria-label': string;
};

const IntervalInput = ({ value, onChange, placeholder, 'aria-label': ariaLabel }: IntervalInputProps) => {
  const [localValue, setLocalValue] = useState(value);
  const latestLocal = useRef(localValue);

  useEffect(() => {
    setLocalValue(value);
    latestLocal.current = value;
  }, [value]);

  return (
    <Input
      size="sm"
      value={localValue}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(event.target.value);
        latestLocal.current = event.target.value;
      }}
      onBlur={() => {
        if (latestLocal.current !== value) {
          onChange(latestLocal.current);
        }
      }}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
};

const getExpectedFromMonth = (intervals: EditableCommissionInterval[], index: number): number | null => {
  if (index === 0) return null;
  const prev = intervals[index - 1];
  const prevTo = Number(prev.toMonth);
  return Number.isFinite(prevTo) ? prevTo + 1 : null;
};

const CommissionIntervalsEditor = ({
  intervals,
  onAddInterval,
  onRemoveInterval,
  onIntervalChange,
}: CommissionIntervalsEditorProps) => (
  <div className="commissions-intervals-editor">
    {intervals.length > 0 && (
      <div className="commissions-intervals-editor-row commissions-intervals-editor-header">
        <P variant="neutral" modifier="weak" type="3" weight="bold" withMargin={false}>
          Desde mes
        </P>
        <P variant="neutral" modifier="weak" type="3" weight="bold" withMargin={false}>
          Hasta mes
        </P>
        <P variant="neutral" modifier="weak" type="3" weight="bold" withMargin={false}>
          Tasa %
        </P>
        <span aria-hidden="true" />
      </div>
    )}

    {intervals.length === 0 ? (
      <P variant="neutral" modifier="weak" withMargin={false}>
        No hay intervalos configurados
      </P>
    ) : (
      <div className="commissions-intervals-editor-rows">
        {intervals.map((interval, intervalIndex) => {
          const expectedFrom = getExpectedFromMonth(intervals, intervalIndex);
          const currentFrom = Number(interval.fromMonth);
          const showHint = expectedFrom !== null;
          const isEmpty = interval.fromMonth === '';
          const isMismatch = showHint && !isEmpty && Number.isFinite(currentFrom) && currentFrom !== expectedFrom;

          return (
            <div
              key={`interval-${intervalIndex}`}
              className="commissions-intervals-editor-row"
            >
              <div>
                <div className="commissions-intervals-editor-field">
                  <IntervalInput
                    value={interval.fromMonth}
                    onChange={(val) => onIntervalChange(intervalIndex, 'fromMonth', val)}
                    placeholder="0"
                    aria-label={`Desde mes intervalo ${intervalIndex + 1}`}
                  />
                </div>
                {showHint && isEmpty && (
                  <span className="commissions-intervals-hint">
                    Debe ser {expectedFrom}
                  </span>
                )}
                {isMismatch && (
                  <span className="commissions-intervals-hint commissions-intervals-hint--warning">
                    Esperado: {expectedFrom}
                  </span>
                )}
              </div>
              <div className="commissions-intervals-editor-field">
                <IntervalInput
                  value={interval.toMonth}
                  onChange={(val) => onIntervalChange(intervalIndex, 'toMonth', val)}
                  placeholder=""
                  aria-label={`Hasta mes intervalo ${intervalIndex + 1}`}
                />
              </div>
              <div className="commissions-intervals-editor-field">
                <IntervalInput
                  value={interval.rate}
                  onChange={(val) => onIntervalChange(intervalIndex, 'rate', val)}
                  placeholder="0,00"
                  aria-label={`Tasa intervalo ${intervalIndex + 1}`}
                />
              </div>
              <span
                className="commissions-delete-icon"
                onClick={() => onRemoveInterval(intervalIndex)}
                title="Eliminar intervalo"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onRemoveInterval(intervalIndex)}
              >
                <Icon name="delete_outline" width={18} height={18} variant="info" />
              </span>
            </div>
          );
        })}
      </div>
    )}

    <div className="commissions-intervals-editor-actions">
      <Button variant="tertiary" size="md" onClick={onAddInterval}>
        + Agregar intervalo
      </Button>
    </div>
  </div>
);

export default CommissionIntervalsEditor;
